import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { ListFooter } from '@/components/ListPager';
import { SearchEmptyState } from '@/components/SearchEmptyState';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePermissions } from '@/hooks/usePermissions';
import { listResource } from '@/lib/api';
import { H5pPackageCard } from './H5pPackageCard';
import {
  type H5pPackage,
  packageCourseCount,
  packageLibraryLabel,
  packageStatusLabel,
  packageStatusModifier,
  packageVersionCount,
} from './h5p-package-utils';

export function H5pPage() {
  const { hasPermission } = usePermissions();
  const canImport = hasPermission(PERMISSIONS.H5P_IMPORTAR);
  const canEdit = hasPermission(PERMISSIONS.H5P_EDITAR);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [tagId, setTagId] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isTrashed, setIsTrashed] = useState(false);
  const [page, setPage] = useState(1);
  const appliedSearch = useDebouncedValue(search, 300).trim();

  useEffect(() => {
    setPage(1);
  }, [appliedSearch]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['h5p', appliedSearch, page],
    queryFn: () =>
      listResource<H5pPackage>('/h5p', {
        page,
        perPage: 20,
        ...(appliedSearch ? { search: appliedSearch } : {}),
      }),
  });

  const total = data?.meta?.total ?? data?.data?.length ?? 0;
  const perPage = data?.meta?.perPage ?? 20;
  const totalPages = data?.meta?.lastPage ?? 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min(page * perPage, total);

  const hasFilters = Boolean(search.trim() || status || categoryId || tagId || isTrashed);

  const packages = useMemo(() => {
    let rows = data?.data ?? [];
    if (status) rows = rows.filter((item) => item.status === status);
    if (isTrashed) rows = rows.filter((item) => item.status === 'archived');
    return rows;
  }, [data?.data, status, isTrashed]);

  function clearFilters() {
    setSearch('');
    setStatus('');
    setCategoryId('');
    setTagId('');
    setIsTrashed(false);
  }

  function toggleTrash() {
    setIsTrashed((current) => !current);
  }

  return (
    <div className="lms-h5p-library lms-site-admin-hub-page font-sans">
      <header className="lms-h5p-library__header">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=plugins" label="Volver a Administración" />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Biblioteca H5P</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Motor de actividades interactivas reutilizables, desacoplado de cursos
            </p>
          </div>
        </div>

        <div className="lms-h5p-library__header-actions">
          <Link to="/h5p/libraries" className="lms-h5p-library__btn lms-h5p-library__btn--outline">
            <span className="material-symbols-outlined" aria-hidden="true">
              library_books
            </span>
            Librerías
          </Link>
          {canImport && (
            <Link to="/h5p/create" className="lms-h5p-library__btn lms-h5p-library__btn--outline">
              <span className="material-symbols-outlined" aria-hidden="true">
                upload_file
              </span>
              Importar paquete .h5p
            </Link>
          )}
          {canEdit && (
            <Link to="/h5p/constructor" className="lms-h5p-library__btn lms-h5p-library__btn--primary">
              <span className="material-symbols-outlined" aria-hidden="true">
                add
              </span>
              Crear actividad
            </Link>
          )}
        </div>
      </header>

      <div className="lms-h5p-library__layout">
        <aside className="lms-h5p-library__sidebar">
          <form method="get" action="/h5p" className="lms-h5p-library__filters" onSubmit={(event) => event.preventDefault()}>
            {viewMode !== 'grid' ? <input type="hidden" name="view" value={viewMode} /> : null}
            {isTrashed ? <input type="hidden" name="trashed" value="1" /> : null}

            <div className="lms-h5p-library__field">
              <label htmlFor="h5p-search">Buscar</label>
              <SearchInput
                id="h5p-search"
                name="search"
                value={search}
                onChange={setSearch}
                placeholder="Buscar actividades..."
                aria-label="Buscar actividades"
                className="lms-h5p-library__search"
              />
            </div>

            <div className="lms-h5p-library__field">
              <label htmlFor="h5p-status">Estado</label>
              <div className="lms-h5p-library__select-wrap">
                <select id="h5p-status" name="status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">Todos</option>
                  <option value="published">Activa</option>
                  <option value="archived">Archivada</option>
                </select>
                <span className="material-symbols-outlined" aria-hidden="true">
                  expand_more
                </span>
              </div>
            </div>

            <div className="lms-h5p-library__field">
              <label htmlFor="h5p-category">Categoría</label>
              <div className="lms-h5p-library__select-wrap">
                <select
                  id="h5p-category"
                  name="category_id"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <option value="">Todas</option>
                </select>
                <span className="material-symbols-outlined" aria-hidden="true">
                  expand_more
                </span>
              </div>
            </div>

            <div className="lms-h5p-library__field">
              <label htmlFor="h5p-tag">Etiqueta</label>
              <div className="lms-h5p-library__select-wrap">
                <select id="h5p-tag" name="tag_id" value={tagId} onChange={(e) => setTagId(e.target.value)}>
                  <option value="">Seleccionar...</option>
                </select>
                <span className="material-symbols-outlined" aria-hidden="true">
                  expand_more
                </span>
              </div>
            </div>

            <div className="lms-h5p-library__filter-actions">
              {hasFilters ? (
                <button type="button" className="lms-h5p-library__filter-clear" onClick={clearFilters}>
                  Limpiar
                </button>
              ) : (
                <span className="lms-h5p-library__filter-clear lms-h5p-library__filter-clear--muted">Limpiar</span>
              )}
              <button
                type="button"
                className={`lms-h5p-library__trash${isTrashed ? ' lms-h5p-library__trash--active' : ''}`}
                onClick={toggleTrash}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  delete
                </span>
                {isTrashed ? 'Salir de papelera' : 'Ver papelera'}
              </button>
            </div>
          </form>
        </aside>

        <div className="lms-h5p-library__content">
          <div className="lms-h5p-library__toolbar">
            <p className="lms-h5p-library__count">
              <strong>{total}</strong> {total === 1 ? 'actividad' : 'actividades'} en la biblioteca H5P
              {isTrashed ? <span className="lms-h5p-library__trashed-note"> (papelera)</span> : null}
            </p>
            <div className="lms-h5p-library__view-toggle" role="group" aria-label="Cambiar vista">
              <button
                type="button"
                className={`lms-h5p-library__view-btn${viewMode === 'grid' ? ' lms-h5p-library__view-btn--active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Vista cuadrícula"
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  grid_view
                </span>
              </button>
              <button
                type="button"
                className={`lms-h5p-library__view-btn${viewMode === 'list' ? ' lms-h5p-library__view-btn--active' : ''}`}
                onClick={() => setViewMode('list')}
                title="Vista lista"
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  view_list
                </span>
              </button>
            </div>
          </div>

          {error && (
            <p className="mb-4 text-sm text-red-600" role="alert">
              {(error as Error).message}
            </p>
          )}

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : packages.length === 0 ? (
            hasFilters ? (
              <SearchEmptyState term={search.trim()} onClear={clearFilters} />
            ) : (
            <div className="lms-h5p-library__empty">
              <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-surface-200 bg-surface-50 px-6 py-12 text-center">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-surface-100 text-surface-400">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                    />
                  </svg>
                </div>
                <h3 className="text-base font-semibold text-surface-900">Sin actividades H5P</h3>
                <p className="mt-1 max-w-sm text-sm text-surface-500">
                  Importa un paquete .h5p para comenzar a reutilizarlo en cualquier curso.
                </p>
                {canImport && (
                  <div className="mt-4">
                    <Link to="/h5p/create" className="lms-h5p-library__btn lms-h5p-library__btn--primary">
                      Importar paquete
                    </Link>
                  </div>
                )}
              </div>
            </div>
            )
          ) : viewMode === 'list' ? (
            <div className="lms-h5p-library__list">
              <div className="ds-card">
                <div className="overflow-x-auto overflow-y-visible">
                  <table className="min-w-full divide-y divide-[#dee2e6]">
                    <thead className="bg-[#f8f9fa]">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-dash-on-surface-variant">
                          Actividad
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-dash-on-surface-variant">
                          Librería
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-dash-on-surface-variant">
                          Versión
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-dash-on-surface-variant">
                          Uso en cursos
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-dash-on-surface-variant">
                          Estado
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-dash-on-surface-variant">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#dee2e6] bg-white [&_td]:overflow-visible">
                      {packages.map((pkg) => {
                        const statusModifier = packageStatusModifier(pkg.status);
                        const courseCount = packageCourseCount(pkg);
                        return (
                          <tr key={pkg.id} className="hover:bg-[#f8f9fa]">
                            <td className="px-4 py-3 text-sm">
                              <Link to={`/h5p/${pkg.id}`} className="font-medium hover:text-brand-600">
                                {pkg.title}
                              </Link>
                            </td>
                            <td className="px-4 py-3 text-sm text-dash-on-surface-variant">
                              {packageLibraryLabel(pkg)}
                            </td>
                            <td className="px-4 py-3 text-sm text-dash-on-surface-variant">
                              v{packageVersionCount(pkg)}
                            </td>
                            <td className="px-4 py-3 text-sm text-dash-on-surface-variant">
                              {courseCount} cursos
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`lms-h5p-library__status lms-h5p-library__status--${statusModifier}`}>
                                {packageStatusLabel(pkg.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-sm">
                              <Link to={`/h5p/${pkg.id}`} className="lms-h5p-library__detail-link">
                                Ver detalles
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="lms-h5p-library__grid">
              {packages.map((pkg) => (
                <H5pPackageCard key={pkg.id} pkg={pkg} />
              ))}
            </div>
          )}
          {!isLoading && total > 0 && (
            <ListFooter
              page={page}
              totalPages={totalPages}
              total={total}
              start={rangeStart}
              end={rangeEnd}
              onPageChange={setPage}
            />
          )}
        </div>
      </div>
    </div>
  );
}
